import { Alert, Input, message, Skeleton, Space } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import * as React from 'react';
import InstructionalLevelApiService from '../../../api/InstructionalLevelApiService';
import FeatureFlag from '../../../consts/FeatureFlag';
import * as GetInstructionalLevelDetailsHandler from '../../../handlerModels/GetInstructionalLevelDetailsHandler';
import * as SaveInstructionalLevelDetailsHandler from '../../../handlerModels/SaveInstructionalLevelDetailsHandler';
import InstructionalLevelDTO from '../../../models/InstructionalLevelDTO';
import BaseFormProps from '../../../redux/bases/BaseFormProps';
import BaseFormState from '../../../redux/bases/BaseFormState';
import LookupsUtil from '../../../utils/LookupsUtil';
import ValidationRuleUtil from '../../../utils/ValidationRuleUtil';
import ValidationUtil from '../../../utils/ValidationUtil';
import AuthorizedContent from '../../AuthorizedContent';
import ResetButton from '../../buttons/ResetButton';
import SaveButton from '../../buttons/SaveButton';

interface InstructionalLevelDetailsFormState extends BaseFormState {
  instructionalLevelId: number;
  instructionalLevel: InstructionalLevelDTO | null;
}

interface InstructionalLevelDetailsFormProps extends BaseFormProps {
  instructionalLevelId: number;
  onSave?: (instructionalLevelId: number) => void;
}

class InstructionalLevelDetailsForm extends React.Component<InstructionalLevelDetailsFormProps, InstructionalLevelDetailsFormState> {
  private readonly _formRef = React.createRef<FormInstance>();
  private readonly _formItems = new Map<string, FormItemProps>()
    .set(InstructionalLevelDTO.name, {
      required: true,
      name: InstructionalLevelDTO.name,
      label: 'Name',
      rules: [
        ValidationRuleUtil.required(),
        ValidationRuleUtil.maxLength(150)
      ]
    })
    .set(InstructionalLevelDTO.code, {
      required: true,
      name: InstructionalLevelDTO.code,
      label: 'Code',
      rules: [
        ValidationRuleUtil.required(),
        ValidationRuleUtil.maxLength(10)
      ],
    });

  constructor(props: InstructionalLevelDetailsFormProps) {
    super(props);

    this.state = {
      instructionalLevelId: 0,
      instructionalLevel: InstructionalLevelDTO.create()
    };
  }

  componentDidMount() {
    this.setState({ loading: true });
    this.loadInstructionalLevel();
  }

  componentDidUpdate(prevProps: InstructionalLevelDetailsFormProps) {
    if (this.props.instructionalLevelId && this.props.instructionalLevelId != prevProps.instructionalLevelId) {
      this.setState({ instructionalLevelId: this.props.instructionalLevelId });
      this.loadInstructionalLevel();
    }
  }

  public resetForm = () => {
    this._formRef.current?.resetFields();
    this.setState({ altered: false });
  }

  private loadInstructionalLevel() {
    if (this.props.instructionalLevelId == 0) {
      this.loadNew();
    }
    else {
      this.loadExisting();
    }
  }

  private loadExisting = () => {
    if (this.props.instructionalLevelId) {
      InstructionalLevelApiService.getInstructionalLevelDetails(this.props.instructionalLevelId)
        .then((results: GetInstructionalLevelDetailsHandler.Result) => {
          if (results && results.instructionalLevel?.id) {
            this.setState({ instructionalLevelId: results.instructionalLevel.id, instructionalLevel: results.instructionalLevel, loading: false });
          }

          this.resetForm();
        }).catch(() => {
          this.setState({ error: true });
        }).finally(() => {
          this.setState({ loading: false });
        });
    }
  }

  private loadNew = () => {
    this.setState({ instructionalLevel: InstructionalLevelDTO.create(), loading: false });
  }

  private handleChange = () => {
    this.setState({ altered: true });
  }

  private handleSubmit = () => {
    this.setState({ submitting: true });

    const request = SaveInstructionalLevelDetailsHandler.Request.create({
      instructionalLevelId: this.state.instructionalLevelId,
      instructionalLevel: InstructionalLevelDTO.create(this._formRef ? (this._formRef.current as any).getFieldsValue() : null)
    });

    InstructionalLevelApiService.saveInstructionalLevelDetails(request)
      .then((result: SaveInstructionalLevelDetailsHandler.Result) => {
        this.setState({
          submitted: true,
          instructionalLevel: result?.instructionalLevel,
        });

        if (result?.succeeded) {
          message.success('Saved');

          if (this.props.onSave && result.instructionalLevel?.id) {
            this.props.onSave(result.instructionalLevel.id);
            this.resetForm();
          }

          LookupsUtil.invalidateCache(InstructionalLevelDTO.className);
        }
        else {
          this.setState({
            error: !result?.succeeded,
            message: result?.errors.join('\n'),
            fieldErrors: result?.fieldErrors
          });
          message.error('Instructional Level could not be saved. ' + this.state.message);
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Instructional Level could not be saved.');
      })
      .finally(() => {
        this.setState({ loading: false, submitting: false });
      });
  }

  render() {
    if (this.state.loading) {
      return <Skeleton active={true} />;
    }

    return (
      <Space size="small" direction="vertical">
        {this.renderErrors()}
        <Form
          ref={this._formRef}
          layout="vertical"
          onValuesChange={this.handleChange}
          onFinish={this.handleSubmit} requiredMark={true}>
          <FormItem
            {...this._formItems.get(InstructionalLevelDTO.code)}
            initialValue={this.state.instructionalLevel?.code}
            {...ValidationUtil.getValidation(InstructionalLevelDTO.code, this.state.fieldErrors, this.state.submitted)}>
            <Input disabled={!this.props.isEditing || this.state.loading || this.state.submitting} />
          </FormItem>
          <FormItem
            {...this._formItems.get(InstructionalLevelDTO.name)}
            initialValue={this.state.instructionalLevel?.name}
            {...ValidationUtil.getValidation(InstructionalLevelDTO.name, this.state.fieldErrors, this.state.submitted)}>
            <Input disabled={!this.props.isEditing || this.state.loading || this.state.submitting} />
          </FormItem>

          {this.renderSave()}
        </Form>
      </Space>
    );
  }

  renderSave() {
    if (this.props.isEditing) {
      return (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_INSTRUCTIONAL_LEVEL]}>
          <Space direction={'horizontal'} >
            <SaveButton saving={this.state.saving} />
            <ResetButton onConfirm={this.resetForm} disabled={!this.state.altered} />
          </Space>
        </AuthorizedContent>
      );
    }
  }

  renderErrors() {
    if (this.state.error && this.state.submitted) {
      return <Alert type="error" message='Error' showIcon={true} description='There were errors submitting your request. Please review the fields below.' />;
    }
  }
}

export default InstructionalLevelDetailsForm;
